import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  TextField,
} from "@mui/material";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import { ModalActionButtonStyles } from "../../Common/styles/modal";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { patchParticipantFilter } from "../../../Redux/reducers/participantsSlice";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { ClearIcon, DatePicker } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import { DateTime } from "luxon";
import {
  CommonDrawerStyle,
  DrawerForm,
  DrawerHeader,
} from "../../Common/styles/drawer";
import { getBetweenError } from "../../Common/UI/participantFilterFields/customColumnFilterUtils";

const allowedTypes = [
  "radio",
  "checkbox",
  "dropdown",
  "text",
  "number",
  "date",
  "calculated_field",
  "calculated_field_text",
];

const NUMBER_OPERATORS = [
  { label: "=", value: "eq" },
  { label: "≠", value: "neq" },
  { label: ">", value: "gt" },
  { label: "≥", value: "gte" },
  { label: "<", value: "lt" },
  { label: "≤", value: "lte" },
  { label: "between", value: "between" },
];

function decodeNumberFilter(raw: string) {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.op) return parsed;
  } catch {}
  return { op: "", val: "", min: "", max: "" };
}

function encodeNumberFilter(op: string, val: string, min: string, max: string) {
  if (!op) return "";
  if (op === "between") return JSON.stringify({ op, min, max });
  return JSON.stringify({ op, val });
}
type Props = {
  closeModal: () => void;
  studyId: any;
};
const statusOptions = [
  { label: "Included", value: "included" },
  { label: "Excluded", value: "excluded" },
  { label: "Unverified", value: "unverified" },
  { label: "Withdrawn", value: "withdrawn" },
  { label: "Decide Later", value: "decide_later" },
];
const lockedParticipantOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];
const screenerOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

let schema = yup.object().shape({
  siteIds: yup.array().of(yup.string().optional()),
  status: yup.array().of(yup.string().optional()),
  isLocked: yup.string().optional(),
  screenerCompleted: yup.string().optional(),
  screenerReceived: yup.string().optional(),
  screenerCompletedFrom: yup.string().optional(),
  screenerCompletedTo: yup.string().optional(),
  notesQuery: yup.string().optional(),
}).test(
  "screener-date-range",
  "From date must be on or before To date",
  function (values) {
    const from = values?.screenerCompletedFrom;
    const to = values?.screenerCompletedTo;
    if (from && to && from > to) {
      return this.createError({
        path: "screenerCompletedTo",
        message:
          "Screener Completion Date From must be on or before Screener Completion Date To",
      });
    }
    return true;
  }
);

const ParticipantFilterModal = ({ closeModal, studyId }: Props) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [cohortList, setCohortList] = useState<any[]>([]);
  const [tagList, setTagList] = useState<{ id: string; label: string }[]>([]);

  const {
    siteIds,
    status,
    isLocked,
    screenerCompleted,
    screenerReceived,
    screenerCompletedFrom,
    screenerCompletedTo,
    cohortId,
    tagIds,
    customHeaderValues,
    columns,
    notesQuery,
  } = useAppSelector((state) => state.participants);
  const sitesList = useAppSelector((state) => state.study.sitesList);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [cohortRes, tagRes]: [AxiosResponse, AxiosResponse] =
          await Promise.all([
            http.get(`/study/${studyId}/participant-groups?page=1&size=100`),
            http.get(`/platform-tags`),
          ]);

        const cohortData = cohortRes.data?.data?.data?.map((item: any) => ({
          id: item?.id,
          label: item?.name || "",
        }));
        setCohortList(cohortData || []);

        const tagData = tagRes.data?.data?.map((item: any) => ({
          id: item?.id,
          label: item?.name || "",
        }));
        setTagList(tagData || []);
      } catch (error) {
        errorToastMessage(error as Error);
      } finally {
        setLoading(false);
      }
    };
    if (studyId) {
      fetchData();
    }
  }, [studyId]);

  const submitHandler = async (values: any) => {
    // Block submission if any between filter has invalid range
    for (const cv of values.customHeaderValues || []) {
      const decoded = decodeNumberFilter(cv.value as string);
      if (decoded.op === "between") {
        const min = decoded.min || "";
        const max = decoded.max || "";
        if (min !== "" || max !== "") {
          if (getBetweenError(min, max)) return;
        }
      }
    }

    // Strip incomplete between entries (op set, both fields empty)
    const cleanedCustomHeaderValues = (values.customHeaderValues || []).filter(
      (cv: any) => {
        const decoded = decodeNumberFilter(cv.value as string);
        return !(
          decoded.op === "between" &&
          (decoded.min || "") === "" &&
          (decoded.max || "") === ""
        );
      }
    );

    dispatch(
      patchParticipantFilter({
        siteIds: values?.siteIds,
        status: values?.status,
        isLocked: values?.isLocked,
        screenerCompleted: values?.screenerCompleted,
        screenerReceived: values?.screenerReceived,
        screenerCompletedFrom: values?.screenerCompletedFrom,
        screenerCompletedTo: values?.screenerCompletedTo,
        cohortId: values?.cohortId,
        tagIds: values?.tagIds,
        notesQuery: values?.notesQuery,
        customHeaderValues: cleanedCustomHeaderValues,
      })
    );
    closeModal();
  };
  const handleCustomFieldChange = (
    fieldKey: string,
    value: string | string[],
    currentValues: { key: string; value: string | string[] }[],
    setFieldValue: (field: string, value: any) => void
  ) => {
    const isEmpty = Array.isArray(value) ? value.length === 0 : value === "";
    const existingIndex = currentValues.findIndex(
      (val) => val.key === fieldKey
    );

    let updated: { key: string; value: string | string[] }[];

    if (isEmpty) {
      updated = currentValues.filter((val) => val.key !== fieldKey);
    } else if (existingIndex >= 0) {
      updated = currentValues.map((val, idx) =>
        idx === existingIndex ? { ...val, value } : val
      );
    } else {
      updated = [...currentValues, { key: fieldKey, value }];
    }

    setFieldValue("customHeaderValues", updated);
  };

  const handleNumberFieldChange = (
    fieldKey: string,
    op: string,
    val: string,
    min: string,
    max: string,
    currentValues: { key: string; value: string | string[] }[],
    setFieldValue: (field: string, value: any) => void
  ) => {
    const encoded = encodeNumberFilter(op, val, min, max);
    const existingIndex = currentValues.findIndex((v) => v.key === fieldKey);
    let updated: { key: string; value: string | string[] }[];
    if (!encoded) {
      updated = currentValues.filter((v) => v.key !== fieldKey);
    } else if (existingIndex >= 0) {
      updated = currentValues.map((v, i) =>
        i === existingIndex ? { key: fieldKey, value: encoded } : v
      );
    } else {
      updated = [...currentValues, { key: fieldKey, value: encoded }];
    }
    setFieldValue("customHeaderValues", updated);
  };

  return (
    <Box sx={CommonDrawerStyle}>
      <DrawerHeader closeDrawer={closeModal} title={"Filter By"} />
      {!loading ? (
        <Formik
          enableReinitialize
          initialValues={{
            siteIds: siteIds || [],
            status: status || [],
            isLocked: isLocked || "",
            screenerCompleted: screenerCompleted || "",
            screenerReceived: screenerReceived || "",
            screenerCompletedFrom: screenerCompletedFrom || "",
            screenerCompletedTo: screenerCompletedTo || "",
            cohortId: cohortId || [],
            tagIds: tagIds || [],
            notesQuery: notesQuery || "",
            customHeaderValues: customHeaderValues || [],
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ errors, touched, setFieldValue, values }) => {
            const betweenErrors: Record<string, string> = {};
            for (const cv of values.customHeaderValues) {
              const decoded = decodeNumberFilter(cv.value as string);
              if (decoded.op === "between") {
                const min = decoded.min || "";
                const max = decoded.max || "";
                if (min !== "" || max !== "") {
                  const err = getBetweenError(min, max);
                  if (err) betweenErrors[cv.key] = err;
                }
              }
            }
            const hasBetweenErrors = Object.keys(betweenErrors).length > 0;
            return (
            <Form style={DrawerForm}>
              <Box sx={{ overflow: "auto", pr: 0.5 }}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Site</FormLabel>
                  <Select
                    fullWidth
                    multiple
                    value={values.siteIds}
                    onChange={(e) => {
                      setFieldValue("siteIds", e.target.value);
                    }}
                    error={touched?.siteIds && errors?.siteIds ? true : false}
                    displayEmpty
                    renderValue={
                      values.siteIds.length > 0
                        ? (selected) =>
                            selected
                              .map((id) => {
                                const site = sitesList.find(
                                  (site: any) => site.id === id
                                );
                                return site ? site.name : "";
                              })
                              .join(", ")
                        : () => (
                            <Typography sx={{ color: "#c1cccf" }}>
                              Select
                            </Typography>
                          )
                    }
                    endAdornment={
                      values.siteIds?.length > 0 && (
                        <InputAdornment
                          sx={{ marginRight: "10px" }}
                          position="end"
                        >
                          <IconButton
                            onClick={() => {
                              setFieldValue("siteIds", []);
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {sitesList.length > 0 ? (
                      sitesList.map((option: any) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="">No options available</MenuItem>
                    )}
                  </Select>
                  <FormHelperText
                    error={touched?.siteIds && errors?.siteIds ? true : false}
                  >
                    {touched?.siteIds && errors?.siteIds
                      ? (errors?.siteIds as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Participant Status</FormLabel>
                  <Select
                    multiple
                    value={values.status}
                    onChange={(e) => {
                      setFieldValue("status", e.target.value);
                    }}
                    fullWidth
                    id="status"
                    displayEmpty
                    renderValue={
                      values.status.length > 0
                        ? (selected) =>
                            (selected as string[])
                              .map(
                                (v) =>
                                  statusOptions.find((o) => o.value === v)
                                    ?.label ?? v
                              )
                              .join(", ")
                        : () => (
                            <Typography sx={{ color: "#c1cccf" }}>
                              Select
                            </Typography>
                          )
                    }
                    endAdornment={
                      values.status.length > 0 && (
                        <InputAdornment
                          sx={{ marginRight: "10px" }}
                          position="end"
                        >
                          <IconButton
                            onClick={() => {
                              setFieldValue("status", []);
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {statusOptions.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={touched?.status && errors?.status ? true : false}
                  >
                    {touched?.status && errors?.status
                      ? (errors?.status as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Is Participant Locked</FormLabel>
                  <Select
                    value={values.isLocked}
                    onChange={(e) => {
                      setFieldValue("isLocked", e.target.value);
                    }}
                    fullWidth
                    id="is-locked"
                    endAdornment={
                      values.isLocked && (
                        <InputAdornment
                          sx={{ marginRight: "10px" }}
                          position="end"
                        >
                          <IconButton
                            onClick={() => {
                              setFieldValue("isLocked", "");
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {lockedParticipantOptions.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={touched?.isLocked && errors?.isLocked ? true : false}
                  >
                    {touched?.isLocked && errors?.isLocked
                      ? (errors?.isLocked as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Screener Completed</FormLabel>
                  <Select
                    value={values.screenerCompleted}
                    onChange={(e) => {
                      setFieldValue("screenerCompleted", e.target.value);
                    }}
                    fullWidth
                    id="screener-completed"
                    endAdornment={
                      values.screenerCompleted && (
                        <InputAdornment
                          sx={{ marginRight: "10px" }}
                          position="end"
                        >
                          <IconButton
                            onClick={() => {
                              setFieldValue("screenerCompleted", "");
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {screenerOptions.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText> </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Email Received</FormLabel>
                  <Select
                    value={values.screenerReceived}
                    onChange={(e) => {
                      setFieldValue("screenerReceived", e.target.value);
                    }}
                    fullWidth
                    id="screener-received"
                    endAdornment={
                      values.screenerReceived && (
                        <InputAdornment
                          sx={{ marginRight: "10px" }}
                          position="end"
                        >
                          <IconButton
                            onClick={() => {
                              setFieldValue("screenerReceived", "");
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {screenerOptions.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText> </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Screener Completion Date From</FormLabel>
                  <DatePicker
                    format="dd/MM/yyyy"
                    maxDate={DateTime.now()}
                    value={
                      values.screenerCompletedFrom
                        ? DateTime.fromISO(values.screenerCompletedFrom)
                        : null
                    }
                    onChange={(newValue) => {
                      setFieldValue(
                        "screenerCompletedFrom",
                        newValue ? newValue.toFormat("yyyy-MM-dd") : ""
                      );
                    }}
                    slotProps={{
                      field: {
                        clearable: true,
                        onClear: () => setFieldValue("screenerCompletedFrom", ""),
                      },
                    }}
                  />
                  <FormHelperText> </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Screener Completion Date To</FormLabel>
                  <DatePicker
                    format="dd/MM/yyyy"
                    maxDate={DateTime.now()}
                    value={
                      values.screenerCompletedTo
                        ? DateTime.fromISO(values.screenerCompletedTo)
                        : null
                    }
                    onChange={(newValue) => {
                      setFieldValue(
                        "screenerCompletedTo",
                        newValue ? newValue.toFormat("yyyy-MM-dd") : ""
                      );
                    }}
                    slotProps={{
                      field: {
                        clearable: true,
                        onClear: () => setFieldValue("screenerCompletedTo", ""),
                      },
                    }}
                  />
                  <FormHelperText
                    error={
                      touched?.screenerCompletedTo &&
                      errors?.screenerCompletedTo
                        ? true
                        : false
                    }
                  >
                    {touched?.screenerCompletedTo && errors?.screenerCompletedTo
                      ? (errors?.screenerCompletedTo as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>User Group</FormLabel>
                  <Select
                    multiple
                    value={values.cohortId}
                    onChange={(e) => {
                      setFieldValue("cohortId", e.target.value);
                    }}
                    fullWidth
                    id="cohortId"
                    displayEmpty
                    renderValue={
                      values.cohortId.length > 0
                        ? (selected) =>
                            (selected as string[])
                              .map(
                                (id) =>
                                  cohortList.find((o) => o.id === id)?.label ??
                                  id
                              )
                              .join(", ")
                        : () => (
                            <Typography sx={{ color: "#c1cccf" }}>
                              Select
                            </Typography>
                          )
                    }
                    endAdornment={
                      values.cohortId.length > 0 && (
                        <InputAdornment
                          sx={{ marginRight: "10px" }}
                          position="end"
                        >
                          <IconButton
                            onClick={() => {
                              setFieldValue("cohortId", []);
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  >
                    {cohortList.map((option: any) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={touched?.cohortId && errors?.cohortId ? true : false}
                  >
                    {touched?.cohortId && errors?.cohortId
                      ? (errors?.cohortId as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Tags</FormLabel>
                  <Autocomplete
                    multiple
                    id="tagIds"
                    options={tagList}
                    getOptionLabel={(option) => option.label}
                    value={tagList.filter((t) => values.tagIds.includes(t.id))}
                    onChange={(_, selected) => {
                      setFieldValue(
                        "tagIds",
                        selected.map((t) => t.id)
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.label}
                          size="small"
                          {...getTagProps({ index })}
                          key={option.id}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          values.tagIds.length === 0 ? "Search tags..." : ""
                        }
                      />
                    )}
                  />
                  <FormHelperText> </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Search in Notes</FormLabel>
                  <TextField
                    value={values.notesQuery}
                    onChange={(e) => {
                      setFieldValue("notesQuery", e.target.value);
                    }}
                    fullWidth
                    id="notesQuery"
                    placeholder="Search text within notes"
                    InputProps={{
                      endAdornment: values.notesQuery !== "" && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              setFieldValue("notesQuery", "");
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormHelperText> </FormHelperText>
                </FormControl>
                {columns?.map((item, index) => {
                  if (!allowedTypes.includes(item.questionType)) return null;

                  const selected = values.customHeaderValues.find(
                    (val) => val.key === item.varname
                  ) || { value: "" };
                  const displayLabel =
                    item.questionLabel || item.label || item.varname;

                  if (
                    item.questionType === "number" ||
                    item.questionType === "calculated_field"
                  ) {
                    const decoded = decodeNumberFilter(selected.value as string);
                    const { op = "", val = "", min = "", max = "" } = decoded;
                    const hasValue =
                      op &&
                      (op === "between"
                        ? min !== "" || max !== ""
                        : val !== "");
                    return (
                      <FormControl
                        key={item.varname}
                        sx={{ ...InputWrapper, mb: 2 }}
                      >
                        <FormLabel sx={LabelStyle}>{item.label}</FormLabel>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                          <Select
                            value={op}
                            onChange={(e) =>
                              handleNumberFieldChange(
                                item.varname,
                                e.target.value,
                                val,
                                min,
                                max,
                                values.customHeaderValues,
                                setFieldValue
                              )
                            }
                            displayEmpty
                            sx={{ minWidth: 90 }}
                            renderValue={
                              op
                                ? undefined
                                : () => (
                                    <Typography sx={{ color: "#c1cccf" }}>
                                      Op
                                    </Typography>
                                  )
                            }
                          >
                            {NUMBER_OPERATORS.map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>

                          {op === "between" ? (
                            <>
                              <TextField
                                value={min}
                                onChange={(e) =>
                                  handleNumberFieldChange(
                                    item.varname,
                                    op,
                                    val,
                                    e.target.value,
                                    max,
                                    values.customHeaderValues,
                                    setFieldValue
                                  )
                                }
                                placeholder="Min"
                                type="number"
                                error={!!betweenErrors[item.varname]}
                                sx={{ flex: 1 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", mt: 1.5 }}
                              >
                                and
                              </Typography>
                              <TextField
                                value={max}
                                onChange={(e) =>
                                  handleNumberFieldChange(
                                    item.varname,
                                    op,
                                    val,
                                    min,
                                    e.target.value,
                                    values.customHeaderValues,
                                    setFieldValue
                                  )
                                }
                                placeholder="Max"
                                type="number"
                                error={!!betweenErrors[item.varname]}
                                sx={{ flex: 1 }}
                              />
                            </>
                          ) : (
                            <TextField
                              value={val}
                              onChange={(e) =>
                                handleNumberFieldChange(
                                  item.varname,
                                  op,
                                  e.target.value,
                                  min,
                                  max,
                                  values.customHeaderValues,
                                  setFieldValue
                                )
                              }
                              placeholder="Value"
                              type="number"
                              disabled={!op}
                              sx={{ flex: 1 }}
                            />
                          )}

                          {hasValue && (
                            <IconButton
                              onClick={() =>
                                setFieldValue(
                                  "customHeaderValues",
                                  values.customHeaderValues.filter(
                                    (v) => v.key !== item.varname
                                  )
                                )
                              }
                              sx={{ mt: 0.5 }}
                            >
                              <ClearIcon />
                            </IconButton>
                          )}
                        </Box>
                        {betweenErrors[item.varname] && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5 }}
                          >
                            {betweenErrors[item.varname]}
                          </Typography>
                        )}
                      </FormControl>
                    );
                  }

                  return (
                    <FormControl
                      key={item.varname}
                      sx={{ ...InputWrapper, mb: 2 }}
                    >
                      <FormLabel sx={LabelStyle}>{displayLabel}</FormLabel>

                      {["text", "date", "calculated_field_text"].includes(
                        item?.questionType,
                      ) ? (
                        <TextField
                          value={selected.value}
                          onChange={(e) =>
                            handleCustomFieldChange(
                              item.varname,
                              e.target.value,
                              values.customHeaderValues,
                              setFieldValue
                            )
                          }
                          fullWidth
                          placeholder={`Enter ${displayLabel.toLowerCase()}`}
                          InputProps={{
                            endAdornment: selected.value !== "" && (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setFieldValue(
                                      "customHeaderValues",
                                      values.customHeaderValues.filter(
                                        (val) => val.key !== item.varname
                                      )
                                    );
                                  }}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      ) : (
                        <Select
                          multiple
                          value={Array.isArray(selected.value) ? selected.value : []}
                          onChange={(e) =>
                            handleCustomFieldChange(
                              item.varname,
                              e.target.value as string[],
                              values.customHeaderValues,
                              setFieldValue
                            )
                          }
                          fullWidth
                          displayEmpty
                          renderValue={(selectedIds) => {
                            const ids = selectedIds as string[];
                            if (ids.length === 0) {
                              return (
                                <Typography sx={{ color: "#c1cccf" }}>
                                  Select
                                </Typography>
                              );
                            }
                            return ids
                              .map(
                                (id) =>
                                  (item.questionChoices || []).find(
                                    (c: any) => c.id === id
                                  )?.label ?? id
                              )
                              .join(", ");
                          }}
                          endAdornment={
                            Array.isArray(selected.value) &&
                            selected.value.length > 0 && (
                              <InputAdornment
                                sx={{ marginRight: "10px" }}
                                position="end"
                              >
                                <IconButton
                                  onClick={() => {
                                    setFieldValue(
                                      "customHeaderValues",
                                      values.customHeaderValues.filter(
                                        (val) => val.key !== item.varname
                                      )
                                    );
                                  }}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </InputAdornment>
                            )
                          }
                        >
                          {(item.questionChoices || []).map((option: any) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </FormControl>
                  );
                })}
              </Box>

              <Box sx={{ mt: "auto" }}>
                <Divider />

                <Box sx={{ ...ModalActionButtonStyles, mt: 2, mb: 2 }}>
                  <>
                    <Button
                      onClick={() => {
                        setFieldValue("siteIds", []);
                        setFieldValue("status", []);
                        setFieldValue("isLocked", "");
                        setFieldValue("cohortId", []);
                        setFieldValue("tagIds", []);
                        setFieldValue("screenerCompleted", "");
                        setFieldValue("screenerReceived", "");
                        setFieldValue("screenerCompletedFrom", "");
                        setFieldValue("screenerCompletedTo", "");
                        setFieldValue("notesQuery", "");
                        setFieldValue("customHeaderValues", []);
                      }}
                      variant="outlined"
                    >
                      Clear All
                    </Button>
                    <Button onClick={closeModal} variant="outlined">
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={hasBetweenErrors}
                    >
                      Apply
                    </Button>
                  </>
                </Box>
              </Box>
            </Form>
          );
        }}
        </Formik>
      ) : (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default ParticipantFilterModal;
